import { getTenant } from '../../../theme/tokens'

export type RecoStatus =
  | 'matched'
  | 'suggested'
  | 'partial'
  | 'unmatched'
  | 'exception'
export type RecoConfidence = 'high' | 'medium' | 'low'
export type RecoType = 'receivable' | 'payable'

export interface PaymentRecord {
  id: string
  date: string
  method: string
  reference: string
  grossAmount: number
  feeAmount: number
  netAmount: number
  currency: string
}

export interface DocumentRecord {
  id: string
  type: 'invoice' | 'bill'
  date: string
  dueDate: string
  counterparty: string
  reference: string
  amount: number
  currency: string
}

export interface RecoSuggestion {
  id: string
  status: RecoStatus
  confidence?: RecoConfidence
  confidenceScore?: number
  type: RecoType
  payments: PaymentRecord[]
  documents: DocumentRecord[]
  rationale?: string
  glCategory?: string
  anomalyFlag?: string
}

export interface RecoException {
  id: string
  type: 'duplicate' | 'variance' | 'missing_doc' | 'unidentified' | 'aged'
  description: string
  amount: number
  currency: string
  date: string
  suggestedResolution: string
  relatedPaymentId?: string
  relatedDocumentId?: string
}

// Seed Data for Malaysian Merchant in MYR
export const mockSuggestions: RecoSuggestion[] = [
  {
    id: 'REC-001',
    status: 'suggested',
    confidence: 'high',
    confidenceScore: 98,
    type: 'receivable',
    payments: [
      {
        id: 'PAY-8821',
        date: '2026-06-14T09:30:00Z',
        method: 'DuitNow QR',
        reference: 'DN-992811',
        grossAmount: 1500.0,
        feeAmount: 7.5, // 0.5% MDR
        netAmount: 1492.5,
        currency: 'MYR',
      },
    ],
    documents: [
      {
        id: 'DOC-INV-1042',
        type: 'invoice',
        date: '2026-06-12T00:00:00Z',
        dueDate: '2026-06-26T00:00:00Z',
        counterparty: 'Syarikat Maju Jaya Sdn Bhd',
        reference: 'INV-1042',
        amount: 1500.0,
        currency: 'MYR',
      },
    ],
    rationale:
      'Exact match on gross amount and counterparty name. Fee variance RM7.50 matches expected 0.5% DuitNow QR MDR.',
    glCategory: '4000 - Sales Revenue',
  },
  {
    id: 'REC-002',
    status: 'suggested',
    confidence: 'high',
    confidenceScore: 95,
    type: 'receivable',
    payments: [
      {
        id: 'PAY-8822',
        date: '2026-06-13T14:15:00Z',
        method: 'Credit Card',
        reference: 'CC-AUTH-4412',
        grossAmount: 4500.0,
        feeAmount: 67.5, // 1.5% MDR
        netAmount: 4432.5,
        currency: 'MYR',
      },
    ],
    documents: [
      {
        id: 'DOC-INV-1040',
        type: 'invoice',
        date: '2026-06-10T00:00:00Z',
        dueDate: '2026-06-24T00:00:00Z',
        counterparty: 'Klinik Kesihatan Utama',
        reference: 'INV-1040',
        amount: 2000.0,
        currency: 'MYR',
      },
      {
        id: 'DOC-INV-1041',
        type: 'invoice',
        date: '2026-06-11T00:00:00Z',
        dueDate: '2026-06-25T00:00:00Z',
        counterparty: 'Klinik Kesihatan Utama',
        reference: 'INV-1041',
        amount: 2500.0,
        currency: 'MYR',
      },
    ],
    rationale:
      'Consolidated payment. Gross amount matches sum of INV-1040 and INV-1041. Counterparty matches.',
    glCategory: '4000 - Sales Revenue',
  },
  {
    id: 'REC-003',
    status: 'partial',
    confidence: 'medium',
    confidenceScore: 75,
    type: 'receivable',
    payments: [
      {
        id: 'PAY-8825',
        date: '2026-06-14T11:00:00Z',
        method: 'Bank Transfer',
        reference: 'IBG-00912',
        grossAmount: 5000.0,
        feeAmount: 0,
        netAmount: 5000.0,
        currency: 'MYR',
      },
    ],
    documents: [
      {
        id: 'DOC-INV-1038',
        type: 'invoice',
        date: '2026-06-01T00:00:00Z',
        dueDate: '2026-06-15T00:00:00Z',
        counterparty: 'Mega Holdings Bhd',
        reference: 'INV-1038',
        amount: 10000.0,
        currency: 'MYR',
      },
    ],
    rationale:
      'Partial payment detected. Counterparty matches, but payment is exactly 50% of invoice value.',
    glCategory: '4000 - Sales Revenue',
    anomalyFlag: 'Partial settlement',
  },
  {
    id: 'REC-004',
    status: 'suggested',
    confidence: 'high',
    confidenceScore: 92,
    type: 'payable',
    payments: [
      {
        id: 'PAY-OUT-101',
        date: '2026-06-14T08:00:00Z',
        method: 'Payout',
        reference: 'PO-9912',
        grossAmount: 3200.0,
        feeAmount: 2.0,
        netAmount: 3202.0, // Payout fee added to deduction
        currency: 'MYR',
      },
    ],
    documents: [
      {
        id: 'DOC-BILL-882',
        type: 'bill',
        date: '2026-06-05T00:00:00Z',
        dueDate: '2026-06-19T00:00:00Z',
        counterparty: 'TechSupplies MY',
        reference: 'INV-TS-2026-06',
        amount: 3200.0,
        currency: 'MYR',
      },
    ],
    rationale:
      'Exact match on bill amount. RM2.00 standard payout routing fee applied.',
    glCategory: '5000 - Cost of Goods Sold',
  },
]

export const mockExceptions: RecoException[] = [
  {
    id: 'EXC-001',
    type: 'missing_doc',
    description:
      'Payment received but no matching invoice found in the system.',
    amount: 850.0,
    currency: 'MYR',
    date: '2026-06-13T16:45:00Z',
    suggestedResolution: 'Create new invoice or map to unearned revenue.',
    relatedPaymentId: 'PAY-8830',
  },
  {
    id: 'EXC-002',
    type: 'variance',
    description:
      'Payment amount exceeds invoice amount beyond acceptable fee tolerance.',
    amount: 1200.0,
    currency: 'MYR',
    date: '2026-06-12T10:20:00Z',
    suggestedResolution: 'Review for overpayment or missing secondary invoice.',
    relatedPaymentId: 'PAY-8815',
    relatedDocumentId: 'DOC-INV-1020',
  },
  {
    id: 'EXC-003',
    type: 'duplicate',
    description:
      'Possible duplicate payment for already settled invoice INV-1015.',
    amount: 450.0,
    currency: 'MYR',
    date: '2026-06-14T09:15:00Z',
    suggestedResolution: 'Initiate refund or apply as credit balance.',
    relatedPaymentId: 'PAY-8835',
    relatedDocumentId: 'DOC-INV-1015',
  },
]

export const mockKpis = {
  reconciledCountPct: 84,
  reconciledValuePct: 89,
  unmatchedValue: 14500.5,
  exceptionsCount: 3,
  autoMatchedValue: 128400.0,
}
